import { Color, Spacing } from '@persona-id/design-tokens';
import { startCase } from 'lodash/fp';
import { FC, useState } from 'react';
import styled from 'styled-components';

import JsonSchemaSelect, { Option as OptionType } from 'dashboard/components/JsonSchemaSelect';
import JsonSchemaValue, {
  REACT_SELECT_PORTAL_ID_PREFIX,
} from 'dashboard/components/WorkflowControlPanel/PlatformSchemaMappings/JsonSchemaValue';
import {
  getPlatformObjectTypeFromJsonSchema,
  getTypeDisplayText,
  PlatformJsonSchema,
} from 'dashboard/lib/workflows/platform-object';
import Select, { Option } from 'design-system/components/Select';
import { Label } from 'design-system/components/Text';
import useDebounced from 'lib/hooks/useDebounced';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

const TYPE_SELECT_WIDTH = 100;

const jssStyles = {
  control: () => ({
    height: 'auto',
    borderLeft: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  }),
};

enum ValueType {
  Variable = 'Variable',
  Value = 'Value',
}

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Row = styled(FlexContainer)`
  justify-content: space-between;
`;

const StyledLabel = styled(Label)`
  flex: 1;
  margin-right: ${Spacing[1]};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const RedText = styled.span`
  color: ${Color.CoreRed600};
`;

const DoubleInputContainer = styled(FlexContainer)`
  width: 70%;
`;

const StyledSelect = styled(Select)`
  width: ${TYPE_SELECT_WIDTH}px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const ValueContainer = styled.div`
  width: calc(100% - ${TYPE_SELECT_WIDTH}px);
`;

interface InputRowProps {
  field: string | null;
  id: string;
  inputSchema: PlatformJsonSchema;
  jsonSchema: JsonSchema;
  label: string;
  required?: boolean;
  resettable: boolean;
  tokensToDisplayValue: Record<string, string>;
  value: any;
  onInputChange: (inputConfig: { value: any; field: string | null }) => void;
}

const InputRow: FC<InputRowProps> = ({
  field,
  id,
  inputSchema,
  jsonSchema,
  label,
  required = true,
  resettable,
  tokensToDisplayValue,
  value,
  onInputChange,
}) => {
  const [internalField, setInternalField] = useState(field);
  const [internalValue, setInternalValue] = useState(value);
  const [valueType, setValueType] = useState<string>(
    value != null ? ValueType.Value : ValueType.Variable,
  );

  const [debouncedInputChange] = useDebounced(onInputChange, 250);

  return (
    <Row aria-label={`Input row for ${label}`}>
      <StyledLabel htmlFor={`${REACT_SELECT_PORTAL_ID_PREFIX}${id}`}>
        {required && <RedText>*</RedText>} ({startCase(getTypeDisplayText(inputSchema))}){' '}
        {startCase(label)}
      </StyledLabel>
      <DoubleInputContainer>
        <StyledSelect
          value={valueType}
          triggerProps={{ 'aria-label': 'Select value type' }}
          onValueChange={setValueType}
        >
          <Option value={ValueType.Variable}>{ValueType.Variable}</Option>
          <Option value={ValueType.Value}>{ValueType.Value}</Option>
        </StyledSelect>
        <ValueContainer>
          {valueType === ValueType.Value ? (
            <JsonSchemaValue
              id={id}
              jsonSchema={jsonSchema}
              schemaType={inputSchema.type}
              tokensToDisplayValue={tokensToDisplayValue}
              value={internalValue}
              onValueChange={(newValue: any) => {
                setInternalValue(newValue);
                debouncedInputChange({ value: newValue, field: null });
              }}
            />
          ) : (
            <JsonSchemaSelect
              id={`${REACT_SELECT_PORTAL_ID_PREFIX}${id}`}
              ariaLabel={`Select ${startCase(label)} value`}
              canSelectObjects={inputSchema.type === JsonSchemaTypes.Object}
              isClearable={resettable}
              inputValue={internalField ?? ''}
              jsonSchema={jsonSchema}
              required={required}
              allowedTypes={[getPlatformObjectTypeFromJsonSchema(inputSchema) || inputSchema.type]}
              allowedArrayTypes={
                inputSchema.type === JsonSchemaTypes.Array &&
                inputSchema.items.type !== JsonSchemaTypes.Array
                  ? [
                      getPlatformObjectTypeFromJsonSchema(inputSchema.items) ||
                        inputSchema.items.type,
                    ]
                  : []
              }
              tokensToDisplayValue={tokensToDisplayValue}
              styles={jssStyles}
              onFieldSelect={(option) =>
                onInputChange({ value: null, field: (option as OptionType).value })
              }
              onValueChange={(value) => {
                setInternalField(value as string);
                if (value === '') {
                  onInputChange({ value: null, field: null });
                }
              }}
            />
          )}
        </ValueContainer>
      </DoubleInputContainer>
    </Row>
  );
};

export default InputRow;
